import React, { useState, useEffect } from 'react';
import { Award } from 'lucide-react';
import { fallbackAwards } from '../data/fallbackData';

function AwardRow({ item, index }) {
  const [isVisible, setIsVisible] = useState(false);
  const rowRef = React.useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    let timeoutId;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timeoutId = setTimeout(() => {
            setIsVisible(true);
          }, index * 120 + 80);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -30px 0px',
      }
    );

    if (rowRef.current) {
      observer.observe(rowRef.current);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [index]);

  return (
    <div
      ref={rowRef}
      className={`py-6 sm:py-8 grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-6 items-center hover:bg-[#EAE5DD]/60 transition-all duration-700 ease-out transform px-3 border-b border-[#DED7CC] ${
        isVisible
          ? 'opacity-100 translate-y-0 scale-100'
          : 'opacity-0 translate-y-6 scale-[0.98] pointer-events-none'
      }`}
    >
      {/* Year */}
      <div className="md:col-span-2 font-['Montserrat'] text-sm sm:text-base text-[#9A6B29] font-medium tracking-[0.2em]">
        {item.year}
      </div>

      {/* Title, Product & Competition Name */}
      <div className="md:col-span-7 space-y-1">
        <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-normal text-[#37332F] uppercase tracking-[0.12em]">
          {item.title}
        </h3>
        <p className="text-xs sm:text-sm text-[#5E5852] font-light">
          {item.product}
        </p>
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#7A726A] font-medium block pt-0.5">
          {item.award_name}
        </span>
      </div>

      {/* Medal */}
      <div className="md:col-span-3 flex md:justify-end">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F6F2EB] border border-[#9A6B29]/60 text-[#9A6B29] text-[11px] font-['Montserrat'] uppercase tracking-wider font-medium shadow-2xs">
          <Award className="w-3.5 h-3.5" aria-hidden="true" />
          {item.medal}
        </span>
      </div>
    </div>
  );
}

export default function Recognition() {
  const [awards, setAwards] = useState(fallbackAwards);

  useEffect(() => {
    async function fetchAwards() {
      try {
        const res = await fetch('/_api/public/v2/content/priser?sort=sortering&order=asc');
        if (res.ok) {
          const data = await res.json();
          if (data && data.items && data.items.length > 0) {
            const mapped = data.items.map((item, idx) => ({
              id: item.id || `award-${idx}`,
              year: item.aarstal || item.year || '',
              title: item.titel || item.title || '',
              product: item.produkt || item.product || '',
              award_name: item.konkurrence || item.award_name || '',
              medal: item.medalje_tekst || item.medal || '',
            }));
            setAwards(mapped);
          }
        }
      } catch (err) {
        console.warn('Using fallback awards:', err);
      }
    }

    fetchAwards();
  }, []);

  return (
    <section
      id="recognition"
      className="relative w-full bg-[#F2EEE7] py-28 md:py-36 px-6 md:px-12 text-[#37332F] scroll-mt-20 overflow-hidden"
      aria-label="Awards og anerkendelser"
    >
      {/* Ambient background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#DED7CC]/30 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-7xl relative z-10">
        {/* Section Header with Refined Graphic Typography */}
        <div className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-[#DED7CC] pb-8">
          <div>
            <span className="font-['Montserrat'] text-xs uppercase tracking-[0.3em] text-[#C99850] block mb-2">
              International Hæder
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-5xl md:text-6xl font-light uppercase tracking-[0.2em] text-[#37332F] leading-none">
              Awards & Anerkendelser
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-[#5E5852] font-light max-w-md leading-relaxed">
            International anerkendelse for vores dedikation til kompromisløst håndværk og uforlignelig nordisk smag.
          </p>
        </div>

        {/* Editorial Awards List */}
        <div className="border-t border-[#DED7CC] divide-y divide-[#DED7CC]">
          {awards.map((item, index) => (
            <AwardRow
              key={item.id || item.title + index}
              item={item}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
